import React, {useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {useStore} from 'effector-react';
import i18next from 'i18next';
import {useForm, Controller, useFieldArray, Control, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';
import 'core/yupCustomValidations';

import {getValidGTINSByManufacturer} from 'api/orders';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {XlsxFileForm, FileFormPropsCallback} from 'components/common/XlsxFileForm';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {Select} from 'components/ui-kit/Select';
import {
    stepChanged,
    productsChanged,
    products$,
    file$,
    fileChanged,
    fetchGtinInfoEvent,
    firstStep$,
} from 'models/orders/create';
import {balance$} from 'models/profile/billing';
import {colors} from 'theme';
import {OrderProductsData} from 'types';
import {GtinOrderListItem, PackageTypes} from 'types/gtins';
import {downloadXlsx} from 'utils/downloadXlsx';

import {
    NavigationButtonsContainer,
    ProductGroupIconContainer,
    FormContainer,
    Fieldset,
    FormPartTitle,
    CountErrorMessage,
    FormField,
    AvailableCodesContainer,
} from '../styled';

const productsSchema = yup
    .array()
    .of(
        yup
            .object({
                gtin: yup.string().required(`${i18next.t('components.form.required')}: GTIN`),
                quantity: yup
                    .number()
                    .typeError(
                        `${i18next.t('components.form.wrong')} ${i18next.t(
                            'pages.orders.form.quantity',
                        )}`,
                    )
                    .positive(
                        `${i18next.t('components.form.wrong')} ${i18next.t(
                            'pages.orders.form.quantity',
                        )}: ${'${value}'}`,
                    )
                    .integer(
                        `${i18next.t('components.form.wrong')} ${i18next.t(
                            'pages.orders.form.quantity',
                        )}: ${'${value}'}`,
                    )
                    .max(
                        10000,
                        `${i18next.t('components.form.wrong')} ${i18next.t(
                            'pages.orders.form.quantity',
                        )}: ${'${value}'}`,
                    )
                    .required(
                        `${i18next.t('components.form.required')}: ${i18next.t(
                            'pages.orders.form.quantity',
                        )}`,
                    ),
                type: yup
                    .string()
                    .oneOf(
                        // ['UNIT', 'GROUP'],
                        Object.keys(PackageTypes),
                        `${i18next.t('components.form.wrong')} ${i18next.t(
                            'pages.orders.form.type',
                        )}: ${'${value}'}`,
                    )
                    .required(
                        `${i18next.t('components.form.required')}: ${i18next.t(
                            'pages.orders.form.type',
                        )}`,
                    ),
            })
            .uniqueProperty('gtin', ' '),
    )
    .min(1, `${i18next.t('components.form.min')} 1`)
    .max(10, `${i18next.t('components.form.max')} 10`);

const schema = yup.object().shape({
    products: productsSchema,
});

interface FormItemProps {
    control: Control;
    defaultValue: string;
    name: string;
    setValue?: Function;
    fieldName?: string;
    getValues: Function;
}

export const ProductCode = ({control, setValue, getValues, fieldName, ...props}: FormItemProps) => {
    const products = useStore(products$);
    const {t} = useTranslation();
    const state = useStore(firstStep$);

    const [gtins, setGtins] = React.useState<GtinOrderListItem[]>([]);
    const getGtins = async () => {
        try {
            if (!state.factoryId) return;
            const id = +state.factoryId;
            if (!id || isNaN(id)) return new Error('id is required');
            const response = await getValidGTINSByManufacturer([id]);
            setGtins(response);
        } catch (error) {
            console.error(error);
        }
    };
    useEffect(() => {
        getGtins();
    }, [state]);

    const gtinOptions = React.useMemo(() => {
        if (!gtins.length) return [];
        return gtins.map((val: GtinOrderListItem) => ({
            value: val.gtin,
            label: `${val.gtin} - ${val.productName} - ${
                val.packageType ? t('dictionaries.products.packageType.' + val.packageType) : ''
            }`,
            meta: {
                name: val.productName ?? '',
                productGroupCode: val.productGroupCode ?? '',
                type: val.packageType ?? '',
            },
        }));
    }, [gtins]);

    return (
        <>
            <Controller
                control={control}
                {...props}
                render={({value, onChange}, {invalid}) => {
                    const currentProduct = products.find((item) => item.gtin === value);
                    const productGroupCode =
                        currentProduct?.productGroupCode ||
                        getValues(`${fieldName}.productGroupCode`);
                    return (
                        <div style={{position: 'relative'}}>
                            {!!productGroupCode && (
                                <ProductGroupIconContainer>
                                    <ProductGroupIcon value={productGroupCode} />
                                </ProductGroupIconContainer>
                            )}
                            <Select
                                label={'GTIN'}
                                isClearable
                                defaultOptions
                                value={{
                                    value,
                                    label: value
                                        ? `${value} - ${currentProduct?.productName ||
                                              getValues(`${fieldName}.productName`)} - ${
                                              currentProduct?.type || getValues(`${fieldName}.type`)
                                                  ? t(
                                                        'dictionaries.products.packageType.' +
                                                            (currentProduct?.type ||
                                                                getValues(`${fieldName}.type`)),
                                                    )
                                                  : ''
                                          }`
                                        : value,
                                }}
                                onChange={(val: {
                                    value: string;
                                    meta: {
                                        name: string;
                                        productGroupCode: string;
                                        type: keyof typeof PackageTypes;
                                    };
                                }) => {
                                    val ? onChange(val.value) : onChange(null);
                                    setValue?.(`${fieldName}.productName`, val?.meta?.name);
                                    setValue?.(
                                        `${fieldName}.productGroupCode`,
                                        val?.meta?.productGroupCode,
                                    );
                                    setValue?.(`${fieldName}.type`, val?.meta?.type);
                                }}
                                options={gtinOptions}
                                hasError={invalid}
                            />
                        </div>
                    );
                }}
            />
        </>
    );
};

export const OrderProducts = () => {
    const {t} = useTranslation();
    const products = useStore(products$);
    const balance = useStore(balance$);
    // const productGroup = useStore(productGroup$);
    const file = useStore(file$);
    // const PACK_TYPES = React.useMemo(
    //     () =>
    //         Object.keys(PRODUCT_GROUPS[productGroup || '']?.packs).map((el: string) => ({
    //             label: t(`dictionaries.packs.${el}`),
    //             value: el,
    //         })),
    //     [t, productGroup],
    // );
    const {control, handleSubmit, formState, reset, setValue, getValues} = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            products:
                products.length > 0
                    ? products
                    : [
                          {
                              gtin: '',
                              quantity: '',
                              type: '',
                              productName: '',
                              productGroupCode: '',
                              templateId: '',
                          },
                      ],
        },
    });
    const {fields, append, remove} = useFieldArray({
        control,
        name: 'products',
    });
    const watchedProducts = useWatch({
        control,
        name: 'products',
    });
    const totalQuantity = useMemo(
        () =>
            (watchedProducts as OrderProductsData[])?.reduce(
                (sum, item) => sum + (Number(item.quantity) || 0),
                0,
            ),
        [watchedProducts],
    );

    const codesLeft = useMemo(
        () => (balance - Math.abs(totalQuantity) < 0 ? 0 : balance - Math.abs(totalQuantity)),
        [totalQuantity],
    );

    const productGroupCodeList = useMemo(
        () => new Set((watchedProducts as OrderProductsData[]).map((p) => p.productGroupCode)),
        [watchedProducts],
    );

    const onHandleChanged = ({products}: {products: Array<OrderProductsData>}) => {
        if (products) productsChanged(products);
        stepChanged(2);
    };
    const handleFileChanged = ({data, file: newFile, isValid}: FileFormPropsCallback) => {
        productsChanged(isValid ? data : []);
        fileChanged(newFile);
        if (isValid) {
            stepChanged(2);
            fetchGtinInfoEvent();
        }

        // Reset react-hook-form, then file was deleted
        if (file && !newFile) {
            reset({
                products: [
                    {gtin: '', quantity: '', type: undefined, templateId: '', productGroupCode: ''},
                ],
            });
        }
    };
    return (
        <FormContainer>
            <Grid container spacing={3}>
                <Grid item md={4} xs={12}>
                    <FormPartTitle style={{marginTop: 10}}>
                        {t('pages.orders.form.productListTitle')}
                    </FormPartTitle>
                </Grid>
                <Grid item md={8} xs={12}>
                    <XlsxFileForm
                        horizontal
                        onChange={handleFileChanged}
                        scheme={productsSchema}
                        assessors={{
                            gtin: 'A',
                            quantity: 'B',
                            type: 'C',
                        }}
                        initialValue={file}
                        linkExample="/examples/order_example.xlsx"
                        linkExampleAction={(path) => {
                            downloadXlsx({
                                path,
                                localePath: 'examples.order',
                            });
                        }}
                    />
                </Grid>
            </Grid>
            {!file && (
                <Grid container spacing={3}>
                    <Grid item md={4}>
                        <FormPartTitle>
                            {t('pages.orders.form.productManualListTitle')}
                        </FormPartTitle>
                    </Grid>
                    <Grid item md={8}>
                        <form onSubmit={handleSubmit(onHandleChanged)}>
                            {fields.map((field, index) => {
                                const fieldName = `products[${index}]`;
                                return (
                                    <Fieldset name={fieldName} key={field.id}>
                                        <FormField>
                                            <Grid container spacing={3}>
                                                <Grid item md={5} xs={12}>
                                                    <ProductCode
                                                        control={control}
                                                        defaultValue={field.gtin}
                                                        name={`${fieldName}.gtin`}
                                                        fieldName={fieldName}
                                                        setValue={setValue}
                                                        getValues={getValues}
                                                    />
                                                </Grid>
                                                <Grid item md={5} xs={12}>
                                                    <Controller
                                                        control={control}
                                                        name={`${fieldName}.quantity`}
                                                        defaultValue={field.quantity}
                                                        render={(props, {invalid}) => (
                                                            <Input
                                                                {...props}
                                                                label={t(
                                                                    'pages.orders.form.quantity',
                                                                )}
                                                                hasError={invalid}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                {/* For submit on enter */}
                                                <input
                                                    type="submit"
                                                    disabled={
                                                        totalQuantity >= balance ||
                                                        (formState.isValid === false &&
                                                            !products.length) ||
                                                        (!!file && !products.length)
                                                    }
                                                    hidden
                                                />
                                                <div style={{display: 'none'}}>
                                                    <Controller
                                                        control={control}
                                                        name={`${fieldName}.type`}
                                                        defaultValue={'PACK'}
                                                        render={() => (
                                                            <input readOnly value="PACK" />
                                                        )}
                                                    />
                                                    <Controller
                                                        control={control}
                                                        name={`${fieldName}.productName`}
                                                        render={(props) => (
                                                            <input value={props.value} readOnly />
                                                        )}
                                                    />
                                                    <Controller
                                                        control={control}
                                                        name={`${fieldName}.productGroupCode`}
                                                        render={(props) => (
                                                            <input value={props.value} />
                                                        )}
                                                    />
                                                </div>
                                                <Grid item md={2} xs={12}>
                                                    {index !== 0 && (
                                                        <CrossIcon
                                                            style={{
                                                                width: 12,
                                                                height: 12,
                                                                marginLeft: 10,
                                                                marginBottom: -3,
                                                                cursor: 'pointer',
                                                                marginTop: 37,
                                                                fill: colors.danger,
                                                            }}
                                                            onClick={() => remove(index)}
                                                        />
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </FormField>
                                    </Fieldset>
                                );
                            })}
                            <Button
                                type="button"
                                style={{width: 200}}
                                variant={ButtonVariant.Normal}
                                disabled={fields.length >= 10}
                                onClick={() => {
                                    append({gtin: '', quantity: ''});
                                }}
                            >
                                + {t('pages.orders.form.add')}
                            </Button>
                            <AvailableCodesContainer
                                noAvailable={balance === 0 || balance < Math.abs(totalQuantity)}
                            >
                                {t('components.form.codesAvailableLeft')}: {codesLeft}
                            </AvailableCodesContainer>
                            {productGroupCodeList.size > 1 && (
                                <AvailableCodesContainer noAvailable>
                                    {t('components.form.onlyOneProductGroup')}
                                </AvailableCodesContainer>
                            )}
                            {fields.length >= 10 && (
                                <CountErrorMessage>{`${i18next.t(
                                    'components.form.max',
                                )} 10`}</CountErrorMessage>
                            )}
                        </form>
                    </Grid>
                </Grid>
            )}
            <NavigationButtonsContainer>
                <Button
                    style={{width: 'auto'}}
                    variant={ButtonVariant.Normal}
                    onClick={() => stepChanged(0)}
                >
                    {t('components.form.prevStep')}
                </Button>
                <Button
                    style={{width: 'auto'}}
                    variant={ButtonVariant.Normal}
                    type="submit"
                    disabled={
                        productGroupCodeList.size > 1 ||
                        totalQuantity >= balance ||
                        (formState.isValid === false && !products.length) ||
                        (!!file && !products.length)
                    }
                    onClick={handleSubmit(onHandleChanged)}
                >
                    {t('components.form.nextStep')}
                </Button>
            </NavigationButtonsContainer>
        </FormContainer>
    );
};
